import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Layout,
  Card,
  Button,
  List,
  Space,
  Tag,
  Typography,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  message
} from 'antd'
import {
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'

const { Title, Text } = Typography
const { Content } = Layout
const { Option } = Select

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
`

const StyledContent = styled(Content)`
  padding: 24px;
  background: #fff;
  min-height: 100vh;
`

const AdditionalServiceCard = styled(Card)`
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  .ant-card-body {
    padding: 16px;
  }
  .ant-list-item-meta-title {
    margin-bottom: 8px;
  }
`

const AdditionalServicesPage = () => {
  const { businessId } = useParams()
  const goBack = useGoBack('/products')
  const { user } = useAuth()
  const [form] = Form.useForm()

  const [parentProduct, setParentProduct] = useState(null)
  const [additionalServices, setAdditionalServices] = useState([])
  const [products, setProducts] = useState([])
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingService, setEditingService] = useState(null)

  useEffect(() => {
    fetchData()
  }, [businessId])

  const fetchData = async () => {
    try {
      const [servicesRes, allProductsRes] = await Promise.all([
        api.get(`/api/business_B/${businessId}/additional-services`),
        api.get(`/api/business_B/${businessId}/products`)
      ])

      console.log('服務數據:', servicesRes.data)
      setAdditionalServices(
        Array.isArray(servicesRes.data.data) ? servicesRes.data.data : []
      )
      setProducts(allProductsRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
      message.error('獲取數據失敗')
      setAdditionalServices([])
    }
  }

  const handleSubmit = async (values) => {
    try {
      // 使用表單中使用者實際選的「適用服務」，並過濾掉可能殘留的 'all' 假值
      const selectedApplicable = (values.applicableProducts || []).filter(
        (id) => id !== 'all'
      )

      const serviceData = {
        ...values,
        applicableProducts: selectedApplicable,
        incompatibleWith: values.incompatibleWith || []
      }

      if (editingService) {
        await api.put(
          `/api/business_B/${businessId}/additional-services/${editingService._id}`,
          serviceData
        )
        message.success('更新成功')
      } else {
        await api.post(
          `/api/business_B/${businessId}/additional-services`,
          serviceData
        )
        message.success('創建成功')
      }
      setDrawerVisible(false)
      setEditingService(null)
      form.resetFields()
      fetchData()
    } catch (error) {
      console.error('提交失敗:', error)
      message.error('操作失敗')
    }
  }

  const handleDelete = async (serviceId) => {
    try {
      await api.delete(
        `/api/business_B/${businessId}/additional-services/${serviceId}`
      )
      message.success('刪除成功')
      fetchData()
    } catch (error) {
      message.error('刪除失敗')
    }
  }

  const filteredProducts = products.filter((product) => !product.isDisabled)

  const handleApplicableProductsChange = (value) => {
    if (value.includes('all')) {
      const allProductIds = filteredProducts.map((product) => product._id)
      form.setFieldsValue({
        applicableProducts: allProductIds
      })
    } else {
      form.setFieldsValue({
        applicableProducts: value
      })
    }
  }

  return (
    <StyledContent>
      <PageHeader>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack} />
        <Title level={4} style={{ margin: 0 }}>
          加選服務管理
        </Title>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingService(null)
            form.resetFields()
            setDrawerVisible(true)
          }}
        >
          新增加選服務
        </Button>
      </PageHeader>

      {console.log('渲染的服務列表:', additionalServices)}

      <List
        dataSource={additionalServices}
        locale={{ emptyText: '暫無加選服務' }}
        renderItem={(service) => (
          <AdditionalServiceCard
            key={service._id}
            actions={[
              <Button
                type='link'
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingService(service)
                  form.setFieldsValue({
                    name: service.name,
                    description: service.description,
                    price: service.price,
                    duration: service.duration,
                    category: service.category,
                    incompatibleWith: service.incompatibleWith,
                    orderIndex: service.orderIndex,
                    applicableProducts: service.applicableProducts
                  })
                  setDrawerVisible(true)
                }}
              >
                編輯
              </Button>,
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(service._id)}
              >
                刪除
              </Button>
            ]}
          >
            <List.Item.Meta
              title={service.name}
              description={
                <Space direction='vertical' size={8}>
                  <Text>{service.description}</Text>
                  <Space wrap>
                    <Tag color='blue'>{service.category}</Tag>
                    <Tag color='green'>NT$ {service.price}</Tag>
                    <Tag color='orange'>{service.duration} 分鐘</Tag>
                    {service.isEnabled ? (
                      <Tag color='success'>啟用中</Tag>
                    ) : (
                      <Tag color='error'>已停用</Tag>
                    )}
                  </Space>
                </Space>
              }
            />
          </AdditionalServiceCard>
        )}
      />

      <Drawer
        title={editingService ? '編輯加選服務' : '新增加選服務'}
        placement='right'
        width={520}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setEditingService(null)
          form.resetFields()
        }}
        extra={
          <Space>
            <Button
              onClick={() => {
                setDrawerVisible(false)
                setEditingService(null)
                form.resetFields()
              }}
            >
              取消
            </Button>
            <Button type='primary' onClick={() => form.submit()}>
              確定
            </Button>
          </Space>
        }
      >
        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item name='name' label='服務名稱' rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name='description' label='服務描述'>
            <Input.TextArea />
          </Form.Item>

          <Form.Item name='price' label='價格' rules={[{ required: true }]}>
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `NT$ ${value}`}
              parser={(value) => value.replace(/NT\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name='duration'
            label='時長(分鐘)'
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='category' label='類別' rules={[{ required: true }]}>
            <Select>
              {['加強', '升級', '搭配', '其他'].map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='incompatibleWith' label='不相容服務'>
            <Select
              mode='multiple'
              placeholder='選擇不相容服務'
              optionFilterProp='children'
            >
              {filteredProducts.map((product) => (
                <Option key={product._id} value={product._id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='orderIndex' label='排序順序'>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='applicableProducts' label='適用服務'>
            <Select
              mode='multiple'
              placeholder='選擇適用服務'
              optionFilterProp='children'
              onChange={handleApplicableProductsChange}
            >
              <Option key='all' value='all'>
                全部服務
              </Option>
              {filteredProducts.map((product) => (
                <Option key={product._id} value={product._id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
    </StyledContent>
  )
}

export default AdditionalServicesPage
